import { Modal, Stack, Text } from '@mantine/core';
import { type FC, useMemo } from 'react';

import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';

import { describePeriod, parsePriceHistory } from './priceHistory.model';

type HistoryRow = {
    key: string;
    status: 'Current' | 'Past';
    price: string;
    currency: string;
    period: string;
};

/**
 * One vendor mapping's price timeline, most recent first: the price in force now, then
 * every price before it with the dates it applied, where known. Opened from the Price
 * History cell on the vendor mapping grids.
 */
export const PriceHistoryModal: FC<{
    opened: boolean;
    onClose: () => void;
    title: string;
    currentPrice: string | null;
    currency: string | null;
    priceSince: string | null;
    historyText: string | null;
}> = ({ opened, onClose, title, currentPrice, currency, priceSince, historyText }) => {
    const rows = useMemo((): HistoryRow[] => {
        const past = parsePriceHistory(historyText).map(
            (entry, idx): HistoryRow => ({
                key: `past-${idx}`,
                status: 'Past',
                price: entry.price,
                currency: entry.currency ?? currency ?? '',
                period: describePeriod(entry),
            }),
        );
        if (!currentPrice) return past;
        return [
            {
                key: 'current',
                status: 'Current',
                price: currentPrice,
                currency: currency ?? '',
                period: priceSince ? `since ${priceSince}` : 'since an unknown date',
            },
            ...past,
        ];
    }, [historyText, currentPrice, currency, priceSince]);

    const columns: DataSheetColumn<HistoryRow>[] = [
        { key: 'status', header: '', width: 90 },
        { key: 'price', header: 'Price', width: 110, align: 'right' },
        { key: 'currency', header: 'Currency', width: 90 },
        { key: 'period', header: 'Applied', width: 240 },
    ];

    return (
        <Modal opened={opened} onClose={onClose} title={title} size="lg" zIndex={3000}>
            <Stack gap={10}>
                <Text size="13px" c="dimmed">
                    Most recent first. A past price loaded from the brand&apos;s own records has
                    no dates; prices changed in Reparo carry the dates they applied.
                </Text>
                <DataSheet<HistoryRow>
                    data={rows}
                    columns={columns}
                    getRowKey={(r) => r.key}
                    hideHeaderBar
                    maxHeight={360}
                />
            </Stack>
        </Modal>
    );
};
